import { ChevronRight } from 'lucide-react';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { Skeleton } from '@cenorion/ui/components/skeleton';
import { cn } from '@cenorion/ui/lib/utils';

import { ErrorFallback } from '@/core/errors/error-fallback';

import { resultHref, viewAllHref } from '../../services/search-adapter';

import { HighlightMatch } from './highlight-match';
import { useResultText } from './use-result-text';

import type { SearchGroupType, SearchResult, SearchResultGroup } from '../../types';
import type { MouseEvent } from 'react';

const viewAllKey = (type: SearchGroupType) => `${type}:all`;
/** Typed, since two record types may share an id. */
const resultKey = (item: SearchResult) => `${item.type}:${item.id}`;

export type SearchPanelStop = { key: string; href: string };

/**
 * Every stop the arrow keys walk, in order: each group's rows, then its "View all", with where
 * each opens. Keys are stable across answers, so the active stop survives the next one when it
 * is still in it.
 */
export function searchPanelStops(
  groups: readonly SearchResultGroup[],
  query: string,
): SearchPanelStop[] {
  return groups.flatMap((group) => [
    ...group.items.map((item) => ({ key: resultKey(item), href: resultHref(item) })),
    ...(group.total > group.items.length
      ? [{ key: viewAllKey(group.type), href: viewAllHref(group.type, query) }]
      : []),
  ]);
}

/** A click the browser should handle itself: a new tab or window, never this page. */
const opensElsewhere = (event: MouseEvent) =>
  event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey;

/** The DOM id of a stop, for the input's `aria-activedescendant`. */
export function searchPanelStopId(listboxId: string, key: string): string {
  return `${listboxId}-${key}`;
}

export type SearchResultsPanelProps = {
  /** The listbox's id: the input's `aria-controls`, and the prefix of every stop's id. */
  listboxId: string;
  /** What was searched: highlighted in every row, quoted when nothing matches. */
  query: string;
  groups: readonly SearchResultGroup[];
  status: 'loading' | 'error' | 'ready';
  error?: unknown;
  onRetry: () => void;
  /** The stop the arrow keys are on, or null. */
  activeKey: string | null;
  /** A row or "View all" opened in this tab: the field clears and closes. */
  onOpen: () => void;
  className?: string;
};

/**
 * The grouped results under the global search. Presentational: the field owns the query, the
 * active stop and the keys. Every row and "View all" is a link, so Ctrl/Cmd-click opens it in a
 * new tab and leaves this page as it is.
 */
export function SearchResultsPanel({
  listboxId,
  query,
  groups,
  status,
  error,
  onRetry,
  activeKey,
  onOpen,
  className,
}: SearchResultsPanelProps) {
  const { t } = useTranslation();
  const textOf = useResultText();
  const labelId = useId();
  const onClick = (event: MouseEvent) => {
    if (!opensElsewhere(event)) onOpen();
  };

  return (
    <div
      className={cn(
        'flex max-h-[min(34rem,70vh)] flex-col gap-4 overflow-y-auto rounded-xl bg-popover p-4 text-popover-foreground shadow-[0_8px_32px_rgb(0_0_0/0.08)]',
        className,
      )}
    >
      <p className="text-sm text-muted-foreground uppercase">{t('Search.Results')}</p>

      {status === 'loading' ? (
        <div className="flex flex-col gap-4" aria-busy="true">
          {Array.from({ length: 3 }, (_, index) => (
            <div key={index} className="flex flex-col gap-1">
              <Skeleton className="h-5 w-32" />
              <Skeleton className="h-5 w-64" />
            </div>
          ))}
        </div>
      ) : status === 'error' ? (
        <ErrorFallback error={error} variant="widget" onRetry={onRetry} />
      ) : groups.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t('Search.NoResults', { query })}</p>
      ) : (
        <div
          role="listbox"
          id={listboxId}
          aria-label={t('Search.Results')}
          className="flex flex-col gap-4"
        >
          {groups.map((group, index) => (
            <div
              key={group.type}
              role="group"
              aria-labelledby={`${labelId}-${group.type}`}
              // The design's divider between groups, drawn as the next group's edge so the
              // listbox holds only groups and options.
              className={cn('flex flex-col gap-4', index > 0 && 'border-t pt-4')}
            >
              <p id={`${labelId}-${group.type}`} className="text-xs text-muted-foreground">
                {t('Search.GroupCount', {
                  group: t(`Search.Groups.${group.type}`),
                  count: group.total,
                })}
              </p>
              {group.items.map((item) => {
                const { title, detail } = textOf(item);
                const key = resultKey(item);
                return (
                  <Link
                    key={key}
                    to={resultHref(item)}
                    onClick={onClick}
                    id={searchPanelStopId(listboxId, key)}
                    role="option"
                    // Focus stays in the input (`aria-activedescendant`); -1 keeps the option
                    // focusable for assistive tech without adding a Tab stop.
                    tabIndex={-1}
                    aria-selected={activeKey === key}
                    className={cn(
                      '-mx-2 -my-1 flex items-center justify-between gap-4 rounded-md px-2 py-1 hover:bg-muted',
                      activeKey === key && 'bg-muted',
                    )}
                  >
                    <div className="flex min-w-0 flex-col gap-1 text-sm">
                      <span className="truncate font-medium text-foreground">
                        <HighlightMatch text={title} query={query} />
                      </span>
                      <span className="truncate text-muted-foreground">
                        <HighlightMatch text={detail} query={query} />
                      </span>
                    </div>
                    <ChevronRight
                      aria-hidden="true"
                      className="size-4 shrink-0 text-muted-foreground rtl:-scale-x-100"
                    />
                  </Link>
                );
              })}
              {group.total > group.items.length ? (
                <Link
                  to={viewAllHref(group.type, query)}
                  onClick={onClick}
                  id={searchPanelStopId(listboxId, viewAllKey(group.type))}
                  role="option"
                  tabIndex={-1}
                  aria-selected={activeKey === viewAllKey(group.type)}
                  className={cn(
                    '-mx-2 -my-1 self-start rounded-md px-2 py-1 text-sm font-medium text-primary hover:bg-muted',
                    activeKey === viewAllKey(group.type) && 'bg-muted',
                  )}
                >
                  {t('Search.ViewAll')}
                </Link>
              ) : null}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
